import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { DeleteResponseTypename, MutationTypename } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"

import {
  DeleteClarificationRequestDocument,
  DeleteClarificationRequestMutation,
  DeleteClarificationRequestMutationVariables
} from "entities/clarification-requests/graphql/mutations.generated"
import { GetClarificationRequestDocument } from "entities/clarification-requests/graphql/queries.generated"
import z from "zod"

import { evictClarificationRequest } from "../lib/evictClarificationRequest"
import { getQueryDocumentByResourceType } from "../lib/getQueryDocumentByResourceType"
import { updateResourceHasClarificationRequest } from "../lib/updateResourceHasClarificationRequest"
import { ClarificationRequestResourceType } from "../types/ClarificationRequest"

const DeleteClarificationRequestVariablesSchema = z.object({
  resourceType: z.string(),
  id: z.string()
})

export function useDeleteClarificationRequest() {
  const { t } = useTranslation()
  const { cache } = useApolloClient()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.ClarificationRequest.DELETE_CLARIFICATION_REQUEST.Start"
      ),
      success: t(
        "Request.ClarificationRequest.DELETE_CLARIFICATION_REQUEST.Success"
      ),
      failure: t(
        "Request.ClarificationRequest.DELETE_CLARIFICATION_REQUEST.Failure"
      )
    }
  })

  const [apolloMutate, rest] = useApolloMutation<
    DeleteClarificationRequestMutation,
    DeleteClarificationRequestMutationVariables
  >(DeleteClarificationRequestDocument)

  const deleteClarificationRequest = useEvent(
    ({
      resourceType,
      id
    }: {
      resourceType: ClarificationRequestResourceType
      id: string
    }) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      evictClarificationRequest(resourceType, id, cache)
      cache.writeQuery({
        query: GetClarificationRequestDocument,
        variables: { resourceType, id },
        data: { clarificationRequest: null }
      })
      updateResourceHasClarificationRequest(resourceType, id, false, cache)

      return callMutation(apolloMutate, {
        variables: { resourceType, id },
        transform: data => ({ success: data.clarificationRequest.success }),
        schemas: {
          input: DeleteClarificationRequestVariablesSchema
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            clarificationRequest: {
              __typename: DeleteResponseTypename,
              success: true
            }
          },
          refetchQueries: result => {
            if (result.errors) {
              return [
                {
                  query: GetClarificationRequestDocument,
                  variables: { id, resourceType }
                },
                {
                  query: getQueryDocumentByResourceType(resourceType),
                  variables: { id }
                }
              ]
            }
            return []
          },
          onCompleted: () => {
            evictClarificationRequest(resourceType, id, cache)
            cache.writeQuery({
              query: GetClarificationRequestDocument,
              variables: { resourceType, id },
              data: { clarificationRequest: null }
            })
            updateResourceHasClarificationRequest(
              resourceType,
              id,
              false,
              cache
            )
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [deleteClarificationRequest, rest] as [
    typeof deleteClarificationRequest,
    typeof rest
  ]
}
